/* eslint-disable jsx-a11y/role-supports-aria-props */
import React, { useState, useMemo, useEffect, useCallback } from "react";
import { PointsProvider } from './context/PointsContext';
import PointsAnimation from './components/PointsAnimation';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import Fuse from "fuse.js";

// Importa Componentes
import OfertaCard from "./components/OfertaCard";
import Header from "./components/Header";
import HeroCarousel from "./components/HeroCarousel";
import WhatsAppCard from "./components/WhatsAppCard";
import Footer from "./components/Footer";
import DetalhesOferta from "./components/DetalhesOferta";
import UserProfile from "./components/UserProfile";
import Cadastro from "./components/Cadastro";
import ScrollToTop from "./components/ScrollToTop";
import GruposLotados from "./components/GruposLotados";

import OfferSection from "./components/OfferSection";
import RecentOffersPage from "./pages/RecentOffersPage";
import './App.css';

const API_URL = `${process.env.REACT_APP_API_URL}/ofertas`;

const getOfferTimestamp = (item) => {
  if (!item) return 0;
  const val = item.createdAt || item.data_cadastro || item.id || "";
  if (!val) return 0;
  if (/^\d+$/.test(val)) {
    return parseInt(val, 10);
  }
  const t = Date.parse(val);
  return isNaN(t) ? 0 : t;
};

// =========================================================
// Componente de Conteúdo da Home Page
// =========================================================

const OfferListContent = ({
  searchTerm,
  activeCategory,
  filteredOffers,
  user,
  favoriteIds,
  toggleFavorite,
  offerLikesMap = {},
  loadingOffers,
  offersData, // Needed for sections
}) => {
  const navigate = useNavigate();
  // Lógica de Seções para a Home (Só quando não está buscando e na categoria 'Todos')
  const sections = useMemo(() => {
    if (searchTerm !== "" || activeCategory !== "Todos") return null;

    // 1. Recém Lançadas (Top 10 por ID/Data decrescente)
    const recent = [...offersData]
      .sort((a, b) => getOfferTimestamp(b) - getOfferTimestamp(a))
      .slice(0, 10);

    const recentIds = new Set(recent.map(o => o.id));

    // 2. Mais Vistas (Top 10 por views - excluindo as que já estão em Recém Lançadas)
    const popular = [...offersData]
      .filter(o => !recentIds.has(o.id))
      .sort((a, b) => (b.views || 0) - (a.views || 0))
      .slice(0, 10);

    const popularIds = new Set(popular.map(o => o.id));
    const featuredIds = new Set([...recentIds, ...popularIds]);

    // 3. Categorias Dinâmicas (Ordenadas por novidades recentes)
    const interests = JSON.parse(localStorage.getItem('categoryInterests') || '{}');
    const allCategories = [...new Set(offersData.map(o => o.categoria).filter(Boolean))];
    
    // Mapear a data da oferta mais recente de cada categoria
    const categoryLastDateMap = {};
    allCategories.forEach(cat => {
      const catOffers = offersData.filter(o => o.categoria === cat);
      if (catOffers.length > 0) {
        const latest = catOffers.reduce((latest, current) => {
          const currentDate = getOfferTimestamp(current);
          const latestDate = getOfferTimestamp(latest);
          return currentDate > latestDate ? current : latest;
        });
        categoryLastDateMap[cat] = getOfferTimestamp(latest);
      } else {
        categoryLastDateMap[cat] = 0;
      }
    });

    // Ordenar categorias priorizando a identidade de uma farmácia (Medicamentos primeiro)
    const preferredOrder = [
      "Medicamentos",
      "Beleza e Dermocosméticos",
      "Higiene Pessoal",
      "Mamãe e Bebê",
      "Suplementos e Fitness",
      "Equipamentos Médicos",
      "Petshop"
    ];

    const sortedCategories = allCategories.sort((a, b) => {
      const idxA = preferredOrder.indexOf(a);
      const idxB = preferredOrder.indexOf(b);
      
      if (idxA !== -1 && idxB !== -1) return idxA - idxB;
      if (idxA !== -1) return -1;
      if (idxB !== -1) return 1;
      
      const dateCompare = categoryLastDateMap[b] - categoryLastDateMap[a];
      if (dateCompare !== 0) return dateCompare;
      return (interests[b] || 0) - (interests[a] || 0);
    });


    const categorySections = sortedCategories.slice(0, 8).map(cat => ({
      title: cat,
      // Exclui ofertas que já foram exibidas no topo (Recém Lançadas ou Mais Vistas)
      offers: offersData.filter(o => o.categoria === cat && !featuredIds.has(o.id)).slice(0, 10)
    })).filter(s => s.offers.length > 0);

    return {
      recent,
      popular,
      categories: categorySections
    };
  }, [offersData, searchTerm, activeCategory]);

  return (
    <main>
      {/* Show Hero Carousel only when not searching and category is 'Todos' */}
      {searchTerm === "" && activeCategory === "Todos" && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <HeroCarousel />
        </motion.div>
      )}

      <div 
        className="container" 
        style={{ 
          position: 'relative', 
          zIndex: 10, 
          marginTop: 'var(--spacing-md)' 
        }}
      >
        {loadingOffers ? (
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '120px 0', gap: '20px' }}>
            <div className="loading-spinner" style={{ 
              width: '40px', 
              height: '40px', 
              border: '3px solid var(--primary-light)', 
              borderTopColor: 'var(--primary-color)', 
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <div style={{ fontSize: '16px', color: 'var(--text-secondary)', fontWeight: '600', fontFamily: 'var(--font-title)' }}>Preparando as melhores ofertas...</div>
            <style>{`
              @keyframes spin { to { transform: rotate(360deg); } }
            `}</style>
          </div>
        ) : sections ? (
          <div style={{ marginTop: 'var(--spacing-lg)' }}>
            {/* Seção Recém Lançadas */}
            <OfferSection
              title="Recém Lançadas"
              offers={sections.recent}
              user={user}
              favoriteIds={favoriteIds}
              toggleFavorite={toggleFavorite}
              offerLikesMap={offerLikesMap}
              onViewAll={() => navigate("/recentes")}
            />

            {/* Inserir Card do WhatsApp entre as seções principais */}
            <div style={{ margin: 'var(--spacing-xl) 0' }}>
               <WhatsAppCard />
            </div>

            {/* Seção Mais Vistas */}
            <OfferSection
              title="Mais Vistas"
              offers={sections.popular}
              user={user}
              favoriteIds={favoriteIds}
              toggleFavorite={toggleFavorite}
              offerLikesMap={offerLikesMap}
            />

            {/* Seções por Categoria */}
            {sections.categories && sections.categories.map((section, idx) => (
              <OfferSection
                key={section.title || idx}
                title={section.title}
                offers={section.offers}
                user={user}
                favoriteIds={favoriteIds}
                toggleFavorite={toggleFavorite}
                offerLikesMap={offerLikesMap}
              />
            ))}
          </div>
        ) : filteredOffers.length > 0 ? (
          <>
            <h2 style={{ fontSize: '24px', color: '#333', marginBottom: 'var(--spacing-md)', marginTop: 'var(--spacing-lg)' }}>
              {activeCategory === "Todos" ? "Resultado da Busca" : `Ofertas em: ${activeCategory}`}
            </h2>
            <div className="grid grid-cols-1 grid-sm-2 grid-md-3 grid-lg-4 grid-xl-5">
              {filteredOffers.map((oferta, index) => (
                <React.Fragment key={oferta.id}>
                  <OfertaCard
                    oferta={oferta}
                    user={user}
                    isFavorite={favoriteIds.includes(oferta.id)}
                    onFavoriteToggle={toggleFavorite}
                    likes={offerLikesMap[oferta.id] || 0}
                  />
                  {/* Insert WhatsApp card after 4th offer (index 3) and only on search results list */}
                  {index === 3 && (
                    <WhatsAppCard key="whatsapp-card" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </>
        ) : (
          <div style={{ textAlign: "center", padding: "50px", color: "#555", marginTop: '40px' }}>
            <i className="fas fa-search" style={{ fontSize: '48px', color: '#ddd', marginBottom: '16px' }}></i>
            <p>Nenhuma oferta encontrada para sua busca.</p>
          </div>
        )}
      </div>
    </main>
  );
};

// Componente simples para lidar com o Logout
const LogoutHandler = ({ onLogout }) => {
  const hasCalled = React.useRef(false);

  useEffect(() => {
    if (!hasCalled.current) {
      onLogout();
      hasCalled.current = true;
    }
  }, [onLogout]);
  return null;
};

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategory, setActiveCategory] = useState("Todos");
  const [activeSubCategory, setActiveSubCategory] = useState(null);
  const user = null;
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [offersData, setOffersData] = useState(() => {
    try {
      const cached = localStorage.getItem("cachedOffers");
      return cached ? JSON.parse(cached) : [];
    } catch (e) {
      console.error("Erro ao ler cache de ofertas:", e);
      return [];
    }
  });
  const [loadingOffers, setLoadingOffers] = useState(() => {
    try {
      const cached = localStorage.getItem("cachedOffers");
      return !cached; // Se temos cache, não exibe spinner inicial
    } catch (e) {
      return true;
    }
  });
  const [offerLikesMap, setOfferLikesMap] = useState(() => {
    try {
      const cached = localStorage.getItem("cachedLikes");
      return cached ? JSON.parse(cached) : {};
    } catch (e) {
      return {};
    }
  });

  // Lógica de Favoritos (callback)
  const toggleFavorite = useCallback((ofertaId) => {
    setFavoriteIds((prevIds) => {
      if (prevIds.includes(ofertaId)) {
        return prevIds.filter((id) => id !== ofertaId);
      } else {
        return [...prevIds, ofertaId];
      }
    });
  }, []);

  // Função de Logout removida
  const handleLogout = useCallback(() => {
    setFavoriteIds([]);
    navigate("/");
  }, [navigate]);

  // Efeito 1: Busca de Ofertas da API
  useEffect(() => {
    const fetchOffers = async () => {
      try {
        const response = await axios.get(API_URL);
        const transformedData = response.data
          .filter(item => (item.imagem_url && item.imagem_url.trim() !== "") || (item.imagem && item.imagem.trim() !== ""))
          .map(item => {
             let mappedCategory = "Outros";
             const catStr = (item.categoria || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
             const titleStr = (item.titulo || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
             const searchStr = `${catStr} ${titleStr}`;
             
             if (searchStr.match(/(beleza|dermocosmetic|cabelo|pele|shampoo|condicionador|maquiagem|creme|protetor solar|hidratante)/)) mappedCategory = "Beleza e Dermocosméticos";
             else if (searchStr.match(/(bebe|infantil|fralda|gestante|maternidade|chupeta|mamadeira|lenço umedecido|lenco umedecido)/)) mappedCategory = "Mamãe e Bebê";
             else if (searchStr.match(/(higiene|banho|corpo|desodorante|sabonete|absorvente|escova|creme dental|fio dental|antisseptico)/)) mappedCategory = "Higiene Pessoal";
             else if (searchStr.match(/(suplemento|vitamina|fitness|whey|creatina|bcaa|omega 3|colageno|aminoacido|proteina)/)) mappedCategory = "Suplementos e Fitness";
             else if (searchStr.match(/(equipamento|medidor|ortopedia|aparelho|termometro|pressao|inalador|nebulizador|oximetro|balanca)/)) mappedCategory = "Equipamentos Médicos";
             else if (searchStr.match(/(medicamento|saude|farmacia|generico|dor|febre|xarope|comprimido|gotas|pomada|gel|remedio)/)) mappedCategory = "Medicamentos";
             else if (searchStr.match(/(petshop|pet|cachorro|gato|racao|veterinario|antipulgas|carrapaticida)/)) mappedCategory = "Petshop";
             else if (catStr.match(/(casa|moveis|decoracao)/)) mappedCategory = "Ignorar"; 
             else mappedCategory = "Medicamentos"; // Default fallback

             return {
               ...item,
               categoria: mappedCategory,
               precoOriginal: item.preco_original,
               precoDesconto: item.preco_desconto,
               imagem: item.imagem_url || item.imagem,
               validade: item.data_validade,
               link: item.link_afiliado
             };
          })
          .filter(item => item.categoria !== "Ignorar")
          .sort((a, b) => getOfferTimestamp(b) - getOfferTimestamp(a));
        
        setOffersData(transformedData);
        setLoadingOffers(false);
        try {
          localStorage.setItem("cachedOffers", JSON.stringify(transformedData));
        } catch (e) {
          console.error("Erro ao gravar cache de ofertas:", e);
        }
      } catch (error) {
        console.error("Erro ao buscar ofertas:", error);
        setLoadingOffers(false);
        // Mantém as ofertas já cacheadas se o servidor der erro
        setOffersData(prev => prev.length > 0 ? prev : []);
      }
    };

    const fetchOfferLikes = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/offers/likes/all`);
        setOfferLikesMap(response.data);
        try {
          localStorage.setItem("cachedLikes", JSON.stringify(response.data));
        } catch (e) {
          console.error("Erro ao gravar cache de curtidas:", e);
        }
      } catch (error) {
        console.error("Erro ao buscar curtidas:", error);
      }
    };

    // Initial fetch
    fetchOffers();
    fetchOfferLikes();

    // Polling: Refresh every 60 seconds
    const intervalId = setInterval(() => {
      fetchOffers();
      fetchOfferLikes();
    }, 60000);

    // Refresh on Window Focus
    const onFocus = () => {
      fetchOffers();
      fetchOfferLikes();
    };
    window.addEventListener("focus", onFocus);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  // Efeito 2: Lógica de Favoritos (localStorage)
  useEffect(() => {
    if (user) {
      try {
        const storedFavorites = localStorage.getItem("userFavorites");
        if (storedFavorites) {
          const parsedFavorites = JSON.parse(storedFavorites);
          if (Array.isArray(parsedFavorites)) {
            setFavoriteIds(parsedFavorites);
          }
        }
      } catch (error) {
        console.error("Erro ao carregar favoritos do localStorage:", error);
      }
    }
  }, [user]);

  useEffect(() => {
    if (user && favoriteIds.length > 0) {
      localStorage.setItem("userFavorites", JSON.stringify(favoriteIds));
    }
  }, [favoriteIds, user]);

  // Lógica de filtro (useMemo) com Fuse.js e Sinônimos de Categoria
  const filteredOffers = useMemo(() => {
    if (!searchTerm && activeCategory === "Todos") {
      return offersData;
    }

    let result = offersData;

    if (activeCategory !== "Todos") {
      // Função robusta para checar sinônimos entre o menu e os produtos do scraper
      const matchCategory = (offerCat, selectedCat) => {
        if (!offerCat || !selectedCat) return false;
        const oCat = offerCat.trim().toLowerCase();
        const sCat = selectedCat.trim().toLowerCase();
        if (oCat === sCat) return true;

        const synonyms = [
          ["beleza e dermocosmeticos", "beleza e cuidado pessoal", "beleza", "dermocosmeticos", "cuidados com a pele", "cuidado facial"],
          ["mamae e bebe", "bebes", "bebe", "infantil", "gestante", "brinquedos"],
          ["medicamentos", "saude", "farmacia", "genericos"],
          ["higiene pessoal", "higiene", "cuidados pessoais", "banho", "corpo"],
          ["suplementos e fitness", "suplementos", "fitness", "vitaminas", "whey", "esportes"],
          ["equipamentos medicos", "aparelhos medicos", "medidores"],
          ["petshop", "pet", "cachorro", "gato", "veterinario", "pets"]
        ];

        for (const group of synonyms) {
          const normGroup = group.map(g => g.normalize('NFD').replace(/[\u0300-\u036f]/g, ''));
          const normOCat = oCat.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          const normSCat = sCat.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          
          if (normGroup.includes(normOCat) && normGroup.includes(normSCat)) {
            return true;
          }
        }

        return false;
      };

      result = result.filter((oferta) => matchCategory(oferta.categoria, activeCategory));
      if (activeSubCategory) {
        result = result.filter((oferta) => oferta.subcategoria === activeSubCategory);
      }
    }

    if (searchTerm) {
      const fuse = new Fuse(result, {
        keys: ["titulo", "loja", "descricao"],
        threshold: 0.4,
        includeScore: true,
      });

      const fuseResults = fuse.search(searchTerm);
      result = fuseResults.map((res) => res.item);
    }

    return result;
  }, [searchTerm, activeCategory, activeSubCategory, offersData]);

  const favoritesList = useMemo(() => {
    return offersData.filter((oferta) => favoriteIds.includes(oferta.id));
  }, [favoriteIds, offersData]);

  const handleCategoryChange = useCallback((category, subCategory = null) => {
    setActiveCategory(category);
    setActiveSubCategory(subCategory);
    if (location.pathname !== "/") {
      navigate("/");
    }
  }, [location.pathname, navigate]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <ScrollToTop />
      <Header
        onSearchChange={setSearchTerm}
        onCategoryChange={handleCategoryChange}
        activeCategory={activeCategory}
        activeSubCategory={activeSubCategory}
        searchTerm={searchTerm}
      />

      <div style={{ flex: 1 }}>
        <Routes>
          <Route
            path="/"
            element={
              <OfferListContent
                searchTerm={searchTerm}
                activeCategory={activeCategory}
                filteredOffers={filteredOffers}
                user={user}
                favoriteIds={favoriteIds}
                toggleFavorite={toggleFavorite}
                offerLikesMap={offerLikesMap}
                loadingOffers={loadingOffers}
                offersData={offersData}
              />
            }
          />
          <Route
            path="/oferta/:ofertaId"
            element={
              <DetalhesOferta
                user={user}
                offersData={offersData}
                favoriteIds={favoriteIds}
                toggleFavorite={toggleFavorite}
                onCategoryChange={handleCategoryChange}
              />
            }
          />


          <Route
            path="/recentes"
            element={
              <RecentOffersPage 
                offersData={offersData}
                user={user}
                favoriteIds={favoriteIds}
                toggleFavorite={toggleFavorite}
                offerLikesMap={offerLikesMap}
              />
            }
          />
          <Route
            path="/logout"
            element={<LogoutHandler onLogout={handleLogout} />}
          />
        </Routes>
      </div>

      <Footer />
    </div>
  );
}

// Wrapper Component to provide contexts
const AppWrapper = () => {
  return (
    <PointsProvider>
      <Router>
        <PointsAnimation />
        <App />
      </Router>
    </PointsProvider>
  );
};

export default AppWrapper;
